// Inspired by https://github.com/gatsbyjs/gatsby/issues/4662

import React, {forwardRef, useContext} from 'react';
import {Link as GatsbyLink} from 'gatsby';
import {forceTrailingSlash} from '../utils/urlUtils';
import {config} from '../../config';
import {throwDatoButtonExceptionMessage} from '../utils/exception/DatoException';
import {TemplateInfoContext} from '../context/TemplateInfoContext';
import {useIntl} from 'react-intl';
import {LocaleEnum} from '../types/locale';

export type LinkPropType = {
  to: string;
  rel?: string;
  activeStyle?: Record<string, unknown>;
  activeClassName?: string;
  partiallyActive?: boolean;
  className?: string;
  title?: string;
  target?: '_blank' | '_self';
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  dataTestId?: string;
  // Component used with DatoCMS data, model info is required for reporting DatoException
  datoModelInfo?: {
    title: string;
    originalId: string;
    model: {
      originalId: string;
    };
  };
};

export const Link = forwardRef<HTMLAnchorElement, LinkPropType>(function Link(
  {
    children,
    to,
    activeStyle,
    activeClassName,
    partiallyActive,
    className,
    target,
    rel = 'noopener noreferrer',
    dataTestId,
    onClick,
    datoModelInfo,
    ...otherProps
  },
  ref
) {
  const {modelName, slug} = useContext(TemplateInfoContext);
  const {locale} = useIntl() as {locale: LocaleEnum};

  if (datoModelInfo && !to) {
    throwDatoButtonExceptionMessage({name: modelName, slug}, datoModelInfo, locale);
  }

  let href = to.trim();
  let isRelative = !/^(https?|tel|mailto):/.test(href);

  // TODO INTSERVICE-1483
  if (href === 'https://ready2order.com/api/doc') {
    return (
      <a href={href} className={className} rel={rel} ref={ref} data-testid={dataTestId} onClick={onClick} {...otherProps}>
        {children}
      </a>
    );
  }

  if (!isRelative && href !== '') {
    const domain = new URL(config.productionHost).hostname;
    href = href.replace(`www.${domain}`, domain);
    href = href.replace(`http://${domain}`, config.productionHost); //https://ready2order.com
    if (href.startsWith(config.productionHost)) {
      isRelative = true;
      href = href.substring(config.productionHost.length);
    }
  } else {
    href = forceTrailingSlash(href);
  }

  if (!(target === '_blank') && isRelative) {
    //Gatsby does not work with target _blank https://github.com/gatsbyjs/gatsby/issues/13825
    return (
      <GatsbyLink
        to={href}
        className={className}
        activeStyle={activeStyle}
        activeClassName={activeClassName}
        partiallyActive={partiallyActive}
        // Gatsby's <Link> is not fully compatible with React.forwardRef due to type mismatch on `ref`.
        // https://github.com/gatsbyjs/gatsby/issues/34325
        // We might can remove it once the fix is done https://github.com/gatsbyjs/gatsby/pull/37543
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any}
        data-testid={dataTestId}
        onClick={onClick}
        {...otherProps}
      >
        {children}
      </GatsbyLink>
    );
  }
  return (
    <a href={href} className={className} rel={rel} target={target || '_blank'} ref={ref} data-testid={dataTestId} onClick={onClick} {...otherProps}>
      {children}
    </a>
  );
});
